import React, { useState, useEffect } from 'react';
import { UseFormReturn, useWatch, Path } from 'react-hook-form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import { HealthQuestion } from '@features/onboarding/components/6-health-question-form/interfaces';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from '@components/ui/form';
import { Checkbox } from '@components/ui/checkbox';
import { Separator } from '@components/ui/separator';
import { FormMessageSpan } from '@components/ui/form';
import SignatureCanvas from './Signature';

interface PrivacyDisclosureStepProps {
  question: HealthQuestion;
  form: UseFormReturn<HealthQuestionsSchema>;
  errorMessage?: string | null;
  setStepErrorMessage: (msg: string | null) => void;
}

export const PrivacyDisclosureStep: React.FC<PrivacyDisclosureStepProps> = ({
  question,
  errorMessage,
  form,
  setStepErrorMessage,
}) => {
  const [isSigned, setIsSigned] = useState(false);
  const signatureErrorRef = React.useRef<HTMLParagraphElement | null>(null);
  const disclaimerErrorRef = React.useRef<HTMLParagraphElement | null>(null);
  const acceptedErrorRef = React.useRef<HTMLParagraphElement | null>(null);

  const signature = useWatch({
    control: form.control,
    name: 'healthQuestions.privacyDisclosure.signature',
  });
  const accepted = useWatch({
    control: form.control,
    name: 'healthQuestions.privacyDisclosure.accepted',
  });
  const disclaimerAccepted = useWatch({
    control: form.control,
    name: 'healthQuestions.privacyDisclosure.disclaimer',
  });

  const signatureFieldState = form.getFieldState(
    'healthQuestions.privacyDisclosure.signature' as Path<HealthQuestionsSchema>
  );
  const signatureError = signatureFieldState.error?.message as
    | string
    | undefined;

  const handleSignatureChange = (base64: string) => {
    form.setValue('healthQuestions.privacyDisclosure.signature', base64, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.trigger('healthQuestions.privacyDisclosure.signature');
  };

  useEffect(() => {
    form.trigger([
      'healthQuestions.privacyDisclosure.signature',
      'healthQuestions.privacyDisclosure.accepted',
      'healthQuestions.privacyDisclosure.disclaimer',
    ]);

    if (signature && accepted && disclaimerAccepted) {
      setStepErrorMessage(null);
    }
  }, [signature, accepted, disclaimerAccepted, form, setStepErrorMessage]);

  useEffect(() => {
    setStepErrorMessage(null);
  }, []);

  // Scroll to the first visible validation error when it appears
  useEffect(() => {
    // Defer slightly to ensure DOM elements with refs are mounted
    const t = setTimeout(() => {
      if (signatureError && signatureErrorRef.current) {
        signatureErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!signatureError && errorMessage?.includes('disclaimer') && disclaimerErrorRef.current) {
        disclaimerErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!signatureError && errorMessage && !form.getValues('healthQuestions.privacyDisclosure.accepted') && acceptedErrorRef.current) {
        acceptedErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }, 0);
    return () => clearTimeout(t);
  }, [signatureError, errorMessage, form]);

  return (
    <section className="m-0 lg:p-6 p-0 lg:card-shadow">
      <div className="">
        <h3 className="text-lg md:text-[26px] font-extrabold mb-4">
          Please read the following:
        </h3>
        <h4 className="text-sm text-primary md:text-xl font-bold mb-1">
          Disclaimer (Just making sure everything you shared is true and
          complete)
        </h4>

        <p className="text-base mb-4 !font-secondary">
          By continuing, you're giving us the thumbs-up that the info you've
          provided about your health (and your dependents, if they're included)
          is accurate and honest. If anything's missing, incorrect, or
          misleading—well, let's just say it could cause some serious headaches,
          like denied claims or even a canceled policy. Yikes, right?
        </p>

        <p className="text-base mb-4 !font-secondary">
          Not to worry, though! If you want to double-check your answers, just hit the previous button to review. Or, you can click on any section on the progress menu at the bottom to jump right to that section.
        </p>

        <FormField
          control={form.control}
          name="healthQuestions.privacyDisclosure.disclaimer"
          render={({ field }) => (
            <FormItem className="flex items-center space-x-2">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="text-base !text-black !font-secondary">
                THE DISCLAIMER: By checking this box, I acknowledge that I have
                <br /> read, understand, and agree to this statement.
              </FormLabel>
            </FormItem>
          )}
        />
        {!signatureError && errorMessage?.includes('disclaimer') && (
          <p ref={disclaimerErrorRef}className="mt-2 text-left">
            <FormMessageSpan
              text={errorMessage}
            />
          </p>
        )}
      </div>
      <Separator className="mt-4 mb-7" />
      <h4 className="text-lg md:text-xl font-bold mb-2">
        Personal Health Information Disclosure:
      </h4>
      <section className="rounded-lg mb-4">
        <p className="text-[15px] !font-secondary">
          By signing this application, I understand the following: That if any healthcare submitted on this form constitutes fraud or there is intentional misrepresentation of a material fact, any plan may rescind healthcare coverage; in any such case, I understand that the plan will return contributions that have previously been paid as the rescinded coverage, minus claims paid.
        </p>
      </section>

      <h4 className="text-lg md:text-xl font-bold mt-4 mb-2">
        Client Privacy Notification:
      </h4>
      <section className="rounded-lg mb-4">
        <p className="text-[15px] !font-secondary">
          Thank you for completing the requested information. Any information, including non-public personal health information, such as name, address, and social security number, including detailed protected health information provided, will be used solely for the purpose of providing a risk assessment and will be used to provide a health benefit quote to your employer.
        </p>
      </section>

      <h4 className="text-lg md:text-xl font-bold mt-4 mb-2">Signature:</h4>
      <div className="border rounded-lg mb-4 max-w-130 w-full max-h-[108px] max-m:h-70 max-m:max-h-70 h-full overflow-hidden">
        <SignatureCanvas
          key={signature || 'empty-signature'}
          onChange={handleSignatureChange}
          onSignatureFlagChange={setIsSigned}
          initialSignature={signature}
        />
      </div>
      {signatureError && errorMessage && (
        <p ref={signatureErrorRef}className="mt-2 text-left">
          <FormMessageSpan
            text={signatureError}
          />
        </p>
      )}

      <FormField
        control={form.control}
        name="healthQuestions.privacyDisclosure.accepted"
        render={({ field }) => (
          <FormItem className="flex items-center space-x-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                  form.trigger('healthQuestions.privacyDisclosure.accepted');
                }}
              />
            </FormControl>
            <FormLabel className="text-base text-black !font-secondary">
              By checking the box, I acknowledge that I have read, understand,
              and agree to the statement.
            </FormLabel>
          </FormItem>
        )}
      />
      {!signatureError && errorMessage && !form.getValues('healthQuestions.privacyDisclosure.accepted') && (
        <p ref={acceptedErrorRef}className="mt-2 text-left">
          <FormMessageSpan
            text={errorMessage}
          />
        </p>
      )}
    </section>
  );
};
